import { Component, Output, ViewChild, ElementRef, OnInit, EventEmitter } from '@angular/core';

import { MatDialogRef } from '@angular/material/dialog';

import { DialogEvent } from 'src/app/shared/utilities/types.utilities';
import { ApplicationService } from 'src/app/services/app.service';
import { MediaService } from 'src/app/services/media.service';
import { PexipService } from 'src/app/services/pexip.service';
import { BaseComponent } from 'src/app/shared/base.component';

@Component({
    selector: 'app-audio-video-modal',
    templateUrl: './audio-video-modal.component.html',
    styleUrls: ['./audio-video-modal.component.scss'],
})
export class AudioVideoModalComponent extends BaseComponent implements OnInit {
    /** Emitter for user action on the modal */
    @Output() public event = new EventEmitter<DialogEvent>();

    /** Is Audio playing */
    public isAudioPlaying: boolean;
    /** Is video playing */
    public isVideoPlaying: boolean;
    /** Show feedbacks */
    public show_feedback: boolean;
    /** Can test test the mic and camera */
    public is_test_enabled: boolean;
    /** Audio options */
    public audio_options: any[] = [
        { id: 'good', label: 'Good', icon: 'mood' },
        { id: 'bad', label: 'Bad', icon: 'mood_bad' },
        { id: 'none', label: 'No sound', icon: 'mic_off' },
    ];
    /** Video options */
    public video_options: any[] = [
        { id: 'good', label: 'Good', icon: 'mood' },
        { id: 'bad', label: 'Bad', icon: 'mood_bad' },
        { id: 'none', label: 'No video', icon: 'videocam_off' },
    ];
    /** Audio option selected */
    public selected_audio: any = {};
    /** Video option selected */
    public selected_video: any = {};
    /** List of audio devices */
    public audio_devices: any[] = [];
    /** List of video devices */
    public video_devices: any[] = [];
    /** Audio source selected */
    public audio_source: any;
    /** Video source selected */
    public video_source: any;

    /** Audio element */
    @ViewChild('audio', { static: false }) audio: ElementRef;
    /** Video element */
    @ViewChild('video', { static: false }) video: ElementRef;

    constructor(
        private service: ApplicationService,
        private media: MediaService,
        private dialogRef: MatDialogRef<AudioVideoModalComponent>,
        private pexip: PexipService
    ) {
        super();
    }

    public ngOnInit() {
        this.is_test_enabled = true; // !this.pexip.in_call;
        this.enumerateDevices();
    }

    public close() {
        this.stopAll();
        this.dialogRef.close();
    }

    public accept() {
        this.event.emit({
            reason: 'done',
            metadata: {
                audio_tested: this.selected_audio.id,
                video_tested: this.selected_video.id,
                audio_source: this.audio_source,
                video_source: this.video_source,
            },
        });
        this.close();
    }

    /** Enumerate Audio and Video Devices */
    public async enumerateDevices() {
        try {
            const devices = await this.media.enumerateDevices();
            // Retrieve Audio Input
            this.audio_devices = devices.filter((media) => media.kind === 'audioinput');
            // Retrieve Video Input
            this.video_devices = devices.filter((media) => media.kind === 'videoinput');
            const saved_media = this.media.getSavedMedia() || {};
            let exist_audio;
            let exist_video;
            if (saved_media.audio_source) {
                exist_audio = this.audio_devices.find(
                    (el) =>
                        el.deviceId === saved_media.audio_source.deviceId ||
                        el.label === saved_media.audio_source.label
                );
            }
            if (saved_media.video_source) {
                exist_video = this.video_devices.find(
                    (el) =>
                        el.deviceId === saved_media.video_source.deviceId ||
                        el.label === saved_media.video_source.label
                );
            }
            this.audio_source = exist_audio ? exist_audio : this.audio_devices[0];
            this.video_source = exist_video ? exist_video : this.video_devices[0];
        } catch (e) {
            this.service.notifyError(
                'Cannot access microphone or camera. Check that your browser has permission to access microphone and camera.'
            );
            throw e;
        }
    }

    /** Get user medias */
    public getUserMedia(options: { audio: any; video: any }): Promise<any> {
        return this.media.getUserMedia(options).catch((err) => {
            this.service.notifyError(
                'Cannot access microphone or camera. Check that your browser has permission to access microphone and camera.'
            );
            throw new Error(err.name);
        });
    }

    /** Start audio recording and directly play back recording */
    public testAudio() {
        const audio = this.audio.nativeElement;
        this.getUserMedia({ audio: { deviceId: this.audio_source.deviceId }, video: false }).then(
            (stream) => {
                audio.srcObject = stream;
                audio.play();
                this.isAudioPlaying = true;
            }
        );
    }

    /** Stop Audio */
    public stopAudio() {
        const audio = this.audio.nativeElement;
        audio.srcObject.getTracks().forEach((track) => track.stop());
        audio.pause();
        this.isAudioPlaying = false;
    }

    /** Start video */
    public testVideo() {
        const video = this.video.nativeElement;
        this.getUserMedia({ audio: false, video: { deviceId: this.video_source.deviceId } }).then(
            (stream) => {
                video.srcObject = stream;
                this.isVideoPlaying = true;
            }
        );
    }

    /** Stop video */
    public stopVideo() {
        this.video.nativeElement.srcObject.getTracks().forEach((track) => track.stop());
        this.isVideoPlaying = false;
    }

    /** Stop Video and Audio if still recording */
    public stopAll() {
        if (this.audio && this.isAudioPlaying) {
            this.stopAudio();
        }
        if (this.video && this.isVideoPlaying) {
            this.stopVideo();
        }
    }

    /** Select Audio option */
    public selectAudio(option) {
        this.selected_audio = option;
    }

    /** Select Video option */
    public selectVideo(option) {
        this.selected_video = option;
    }
}
